import { useEffect, useLayoutEffect } from 'react';

import debounce from 'lodash/debounce';

export const useActivitiesEffects = ({
  scrollRef,
  shouldScrollToBottom,
  scrollAdjustmentRef,
  userHasScrolled,
  isLoading,
  data,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage
}) => {
  // Scroll to bottom on initial load and after posting a comment
  useEffect(() => {
    if (scrollRef.current && (isLoading || shouldScrollToBottom.current)) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      shouldScrollToBottom.current = false;
    }
  }, [data, isLoading, scrollRef, shouldScrollToBottom]);

  // Adjust scroll position synchronously before browser paint to prevent visible jump
  useLayoutEffect(() => {
    if (scrollAdjustmentRef.current && scrollRef.current) {
      const { scrollTop, scrollHeight } = scrollAdjustmentRef.current;
      const heightDifference = scrollRef.current.scrollHeight - scrollHeight;
      if (heightDifference > 0) {
        scrollRef.current.scrollTop = scrollTop + heightDifference;
      }
      scrollAdjustmentRef.current = null;
    }
  }, [data, scrollAdjustmentRef, scrollRef]);

  // Mark that user has scrolled after the initial programmatic scroll-to-bottom
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || isLoading) return;

    let ignoreNextScroll = true;
    const onScroll = () => {
      if (ignoreNextScroll) {
        ignoreNextScroll = false;
        return;
      }
      userHasScrolled.current = true;
    };

    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, [data, isLoading, scrollRef, userHasScrolled]);

  // Fetch next page when user scrolls to the top
  useEffect(() => {
    if (isLoading || !hasNextPage) return;

    const el = scrollRef.current;
    if (!el) return;

    const handleScroll = debounce(() => {
      if (userHasScrolled.current && el.scrollTop < 50 && hasNextPage && !isFetchingNextPage) {
        scrollAdjustmentRef.current = {
          scrollTop: el.scrollTop,
          scrollHeight: el.scrollHeight
        };
        fetchNextPage();
      }
    }, 200);

    el.addEventListener('scroll', handleScroll);
    return () => {
      handleScroll.cancel();
      el.removeEventListener('scroll', handleScroll);
    };
  }, [
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    scrollAdjustmentRef,
    scrollRef,
    userHasScrolled
  ]);
};
